/**
 * MessageInput - Multiline chat input with Enter-to-send
 */

import React, { useState, useRef } from 'react';
import { Send } from 'lucide-react';
import { motion } from 'framer-motion';

export default function MessageInput({ onSend, disabled, placeholder = "Write a message…" }) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef(null);

  const handleSubmit = () => {
    if (message.trim() && !disabled) {
      onSend(message);
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    setMessage(e.target.value);
    
    // Auto-resize
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  return (
    <div className="p-3 border-t border-white/10 bg-black/10">
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          aria-label="Message input"
          className="flex-1 resize-none rounded-2xl px-4 py-3 bg-white/10 border border-white/15 outline-none text-white placeholder-white/50 focus:ring-2 focus:ring-green-500/50 transition-all max-h-32 overflow-y-auto disabled:opacity-50"
          style={{ minHeight: '48px' }}
        />
        <motion.button
          onClick={handleSubmit}
          disabled={disabled || !message.trim()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Send message"
          className="rounded-full p-3 border border-white/20 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed bg-green-600 hover:bg-green-700 transition-colors"
        >
          <Send className="w-5 h-5" />
        </motion.button>
      </div>
      <p className="text-xs text-white/40 mt-2 px-1">
        <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20 text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20 text-xs">Shift+Enter</kbd> for new line
      </p>
    </div>
  );
}
